import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { Formik, Form, Field } from "formik";
import { Row, Col } from "reactstrap";
import * as _ from "lodash";

const DjangoFormContext = React.createContext();



// Redefining to properly implementation forwardRef
const DjangoFormFunctional = React.forwardRef((props, ref) => {
    const {
        fields: propsFields,
        processFields,
        chunks = 1,
        onSubmit,
        children,
        extraParams = []
    } = props;

    const formikRef = useRef();
    const [fields, setFields] = useState(null);

    React.useImperativeHandle(ref, () => ({
        get values() {
            if (!formikRef.current) return {};
            const { values } = formikRef.current.state;
            return Object.keys(values).reduce((res, key) => {
                let value = values[key];
                if (value && value.hasOwnProperty("value")) {
                    value = value.value;
                }
                return {
                    ...res,
                    [key]: value
                };
            }, {});
        }
    }));

    const computeInitialValues = (fieldsData) => {
        if (fieldsData) {
            return fieldsData.reduce((entities, field) => ({ ...entities, [field.key]: null }), {});
        }
        return null;
    };

    const _computeForm = (form) => {
        if (form) {
            if (form.ordered_fields) {
                return form.ordered_fields
                    .filter(field => !extraParams.includes(field) && field !== "uploaded_file")
                    .map(key => {
                        const res = { ...form.fields[key] };
                        res["key"] = key;
                        return res;
                    });
            }
            return [];
        }
    };

    useEffect(() => {
        if (processFields) {
            setFields(_computeForm(propsFields));
        } else {
            setFields(propsFields);
        }
    }, [propsFields, processFields]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = (values, cmp) => {
        const fakeFn = () => { };
        const fn = onSubmit || fakeFn;
        handleSubmit(fn, values, cmp);
    };

    const handleSubmit = (submitFn, values, { setSubmitting }) => {
        // Manually getting values again to ensure parsed values? 
        // The original code did `values = this.values;`.
        // We can replicate accessing the exposed values getter if strictly needed, 
        // or just rely on Formik values if they are formatted correctly.
        // Original logic re-parsed values from state. Let's do that for consistency.

        // const currentValues = ref.current ? ref.current.values : values;
        // Note: usage of 'ref.current' here is tricky inside the component that defines it.
        // We can just use the internal logic:

        let processedValues = values; // Fallback
        if (formikRef.current) {
            const { values: rawStateValues } = formikRef.current.state;
            processedValues = Object.keys(rawStateValues).reduce((res, key) => {
                let value = rawStateValues[key];
                if (value && value.hasOwnProperty("value")) {
                    value = value.value;
                }
                return {
                    ...res,
                    [key]: value
                };
            }, {});
        }

        submitFn(processedValues);
        setSubmitting(false);
    };

    const _computeField = (field, index, { values, setFieldValue }) => {
        const {
            widget: { input_type, title, attrs, is_required },
            label,
            help_text,
            key,
            choices
        } = field;
        const value = values[key];
        const ops = {
            ...(attrs.hasOwnProperty("maxlength") ? { maxLength: attrs.maxlength } : {}),
            ...(attrs.hasOwnProperty("minlength") ? { minLength: attrs.minlength } : {}),
            name: key,
            title: title,
            type: input_type,
            required: is_required
        };
        const isSelect = input_type === "select" && Array.isArray(choices) && choices.length > 0;
        let input = <Field {...ops} value={value || ""} className="form-control" />;
        if (isSelect) {
            delete ops.type; // Select component doesn't need type="text" etc
            let select_ops = choices.map(({ value, display }) => ({
                value,
                label: display
            }));

            input = (
                <Field
                    {...ops}
                    value={value}
                    component={Select}
                    options={select_ops}
                    isClearable
                    onChange={val => {
                        setFieldValue(key, val, false);
                    }}
                />
            );
        }
        if (input_type === "hidden") {
            return input;
        }
        return (
            <div key={`django_input_${index}`} className="form-group">
                <label>{label}</label>
                {input}
                {help_text && <small className="form-text text-muted">{help_text}</small>}
            </div>
        );
    };

    const iv = computeInitialValues(fields);

    return (
        <div className="django_form">
            <Formik
                ref={formikRef}
                enableReinitialize={true}
                initialValues={iv}
                onSubmit={submit}
            >
                {cpm => {
                    if (fields) {
                        const hiddenFields = fields.filter(f => f.widget.input_type === "hidden");
                        const visibleFields = fields.filter(f => f.widget.input_type !== "hidden");

                        return (
                            <Form>
                                {hiddenFields.map((field) => (
                                    <React.Fragment key={field.key}>
                                        {_computeField(field, 0, cpm)}
                                    </React.Fragment>
                                ))}
                                {_.chunk(visibleFields, chunks).map((fields2, index2) => (
                                    <Row key={`row-${index2}`}>
                                        {fields2.map((field, index) => (
                                            <Col key={`col-${index}`} md={12 / chunks}>
                                                {_computeField(field, index, cpm)}
                                            </Col>
                                        ))}
                                    </Row>
                                ))}
                                <DjangoFormContext.Provider value={cpm}>
                                    {children}
                                </DjangoFormContext.Provider>
                            </Form>
                        );
                    }
                    return null;
                }}
            </Formik>
        </div>
    );
});

export default DjangoFormFunctional;
export const ExtraForm = DjangoFormContext.Consumer;
